import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Dropdown, Input, Table, Tag, Typography } from 'antd';
import { ArrowsClockwise, MapPin, Package, PencilSimple, X } from '@phosphor-icons/react';
import styled from 'styled-components';
import { ConfirmationPopup, ItemImage } from '../../components';
import { InventoryTransferSuggestedListHook } from '../../hooks/inventory';
import { useSuggestedTransferRefresh } from '../../hooks';
import { TransferPopup } from './transfer-popup';
import { PopupHook } from '@hooks/popup';
import { DestinationSuggestionModal } from './destinationSuggestionModal';
import { OriginSuggestionModal } from './originSuggestionModal';
import { BulkSuggestedTransferConfirmModal } from './bulkSuggestedTransferConfirmModal';
import { AlertFooterBar } from '../../elements/FooterAlert';
import { SuggestedTransferQuantityModal } from './suggestedTransferQuantityModal';
import { BoldText, MuteText } from '../../elements';
import {
  CommonConstant,
  CommonUtility,
  InventoryService,
  theme,
  ToastMessage,
} from '../../utility';
import { COLOR } from '@styles/theme';
import {
  getTransferAlertMessage,
  TransferAlertIndicator,
} from './transferAlertIndicator';

const { Text } = Typography;

const priorityColors = {
  medium: theme.colors.yellow,
};

const ItemTitle = styled.div`
  .title {
    max-width: 12vw;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const IconMenuLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const TableToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 12px;
`;

function RefreshSuggestedTransfersButton({ onRefresh, disabled, isProcessing }) {
  return (
    <Button loading={isProcessing} onClick={onRefresh} disabled={disabled}>
      <ArrowsClockwise className="mr-1" size={16} />
      {isProcessing ? 'Refreshing...' : 'Refresh Suggestions'}
    </Button>
  );
}

const toAntSortOrder = sortOrder => {
  if (sortOrder === CommonConstant.sortDirection.ascending) return 'ascend';
  if (sortOrder === CommonConstant.sortDirection.descending) return 'descend';
  return null;
};

export function InventorySuggestedTransferListScreen({ warehouseId }) {
  const [itemToTransfer, setItemToTransfer] = useState(null);
  const [itemToSuggestDestination, setItemToSuggestDestination] = useState(null);
  const [itemToEditQuantity, setItemToEditQuantity] = useState(null);
  const [itemToEditOrigin, setItemToEditOrigin] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteProcessing, setDeleteProcessing] = useState(false);
  const [selectedById, setSelectedById] = useState({});
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const {
    open: openSuggestDestinationPopup,
    handleOpen: handleOpenSuggestDestinationPopup,
    handleClose: handleCloseSuggestDestinationPopup,
  } = PopupHook();

  const {
    data,
    loading,
    totalData,
    filter,
    filterChanged,
    onSortChange,
    pageChanged,
    refresh,
    refreshAsync,
    setSearch,
  } = InventoryTransferSuggestedListHook(warehouseId);
  const { startRefresh, isProcessing } = useSuggestedTransferRefresh(
    warehouseId,
    refresh,
  );
  const activeSortField = filter.sort?.sortBy;
  const activeAntSortOrder = toAntSortOrder(filter.sort?.sortOrder);

  const selectedItems = useMemo(() => Object.values(selectedById), [selectedById]);
  const selectedCount = selectedItems.length;

  useEffect(() => {
    setSelectedById(prev => {
      let changed = false;
      const next = { ...prev };
      data.forEach(item => {
        if (next[item._id] && next[item._id] !== item) {
          next[item._id] = item;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [data]);

  const onBulkSelectToggle = useCallback((item, checked) => {
    setSelectedById(prev => {
      const next = { ...prev };
      if (checked) {
        next[item._id] = item;
      } else {
        delete next[item._id];
      }
      return next;
    });
  }, []);

  const onBulkDeselect = useCallback(suggestionId => {
    setSelectedById(prev => {
      const next = { ...prev };
      delete next[suggestionId];
      return next;
    });
  }, []);

  const handleBulkCompleted = useCallback(() => {
    setSelectedById({});
    refresh();
  }, [refresh]);

  const clearBulkSelection = useCallback(() => {
    setSelectedById({});
  }, []);

  const openDeleteConfirm = item => {
    setPendingDelete(item);
  };

  const closeDeleteConfirm = () => {
    if (!deleteProcessing) setPendingDelete(null);
  };

  const confirmDeleteSuggestedTransfer = async () => {
    if (!pendingDelete?._id) return;

    try {
      setDeleteProcessing(true);
      await InventoryService.deleteSuggestedTransfer(pendingDelete._id);
      ToastMessage.success('Suggested transfer deleted');
      onBulkDeselect(pendingDelete._id);
      setPendingDelete(null);
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setDeleteProcessing(false);
    }
  };

  const deleteMessage =
    pendingDelete &&
    `Delete this suggested transfer?${
      pendingDelete.productId?.p_sku ? ` (${pendingDelete.productId.p_sku})` : ''
    }`;

  const openTransfer = item => {
    setItemToTransfer(item);
  };

  const openSuggestDestination = item => {
    setItemToSuggestDestination(item);
    handleOpenSuggestDestinationPopup();
  };

  const handleQuantityUpdated = item => {
    setSelectedById(prev => {
      if (!prev[item._id]) return prev;
      return {
        ...prev,
        [item._id]: item,
      };
    });
    refresh();
  };

  const onBulkSelectRows = useCallback((items, checked) => {
    setSelectedById(prev => {
      const next = { ...prev };
      items.forEach(item => {
        if (checked) {
          next[item._id] = item;
        } else {
          delete next[item._id];
        }
      });
      return next;
    });
  }, []);

  const selectedCurrentPageCount = useMemo(
    () => data.filter(item => !!selectedById[item._id]).length,
    [data, selectedById],
  );
  const isCurrentPageSelected =
    data.length > 0 && selectedCurrentPageCount === data.length;
  const isCurrentPagePartiallySelected =
    selectedCurrentPageCount > 0 && selectedCurrentPageCount < data.length;

  const columns = [
    {
      title: (
        <Checkbox
          checked={isCurrentPageSelected}
          indeterminate={isCurrentPagePartiallySelected}
          onChange={event => onBulkSelectRows(data, event.target.checked)}
        />
      ),
      key: 'select',
      width: 48,
      align: 'center',
      render: (_, item) => (
        <Checkbox
          checked={!!selectedById[item._id]}
          onChange={event => onBulkSelectToggle(item, event.target.checked)}
        />
      ),
    },
    {
      title: 'Product Description',
      key: 'description',
      width: '25%',
      render: (_, item) => (
        <div className="d-flex align-items-center">
          <ItemImage
            imageKey="p_images"
            displayImageKey="display_image"
            item={item.productId}
            size="mini"
          />
          <ItemTitle className="ml-4">
            <div className="title">{item.productId?.p_title}</div>
            <div className="d-flex">
              <MuteText className="mr-2">SKU</MuteText>
              <BoldText>{item.productId?.p_sku}</BoldText>
            </div>
          </ItemTitle>
        </div>
      ),
    },
    {
      title: 'Priority',
      key: 'priority',
      width: '9%',
      render: (_, item) => (
        <Tag color={priorityColors[item.priority]}>
          {CommonUtility.toTitleCase(item.priority)}
        </Tag>
      ),
    },
    {
      title: 'From',
      key: 'from',
      width: '18%',
      render: (_, item) => (
        <div>
          <Tag color={item.sourceLocationId?.zoneId?.color}>
            {item.sourceLocationId?.zoneId?.code}
          </Tag>
          <br />
          <Tag className="mt-1" color={item.sourceLocationId?.zoneId?.color}>
            {item.sourceLocationId?.code}
          </Tag>
        </div>
      ),
    },
    {
      title: 'To',
      key: 'to',
      width: '18%',
      render: (_, item) =>
        item.destinationLocationId ? (
          <div>
            <Tag color={item.destinationLocationId.zoneId?.color}>
              {item.destinationLocationId.zoneId?.code}
            </Tag>
            <br />
            <Tag className="mt-1" color={item.destinationLocationId.zoneId?.color}>
              {item.destinationLocationId.code}
            </Tag>
          </div>
        ) : (
          <MuteText>N/A</MuteText>
        ),
    },
    {
      title: 'Restock Amount',
      key: 'amount',
      dataIndex: 'suggestedQuantity',
      width: '15%',
      sorter: true,
      sortOrder: activeSortField === 'suggestedQuantity' ? activeAntSortOrder : null,
      render: (_, item) => (
        <>
          <div className="d-flex align-items-center">
            <Text>{item.suggestedQuantity}</Text>
            <MuteText className="ml-1">Units</MuteText>
            <TransferAlertIndicator
              className="ml-1"
              message={getTransferAlertMessage(item)}
            />
          </div>
          {item.suggestedQuantityCartons ? (
            <div>
              <Text>{item.suggestedQuantityCartons}</Text>
              <MuteText className="ml-1">Cartons</MuteText>
            </div>
          ) : null}
        </>
      ),
    },
    {
      title: '',
      key: 'action',
      width: '25%',
      align: 'right',
      render: (_, item) => (
        <ActionButtons>
          <Dropdown
            trigger={['hover']}
            menu={{
              items: [
                {
                  key: 'quantity',
                  label: (
                    <IconMenuLabel>
                      <PencilSimple size={14} />
                      Edit Qty
                    </IconMenuLabel>
                  ),
                  onClick: () => setItemToEditQuantity(item),
                },
                {
                  key: 'origin',
                  label: (
                    <IconMenuLabel>
                      <Package size={14} />
                      Edit Origin
                    </IconMenuLabel>
                  ),
                  onClick: () => setItemToEditOrigin(item),
                },
                {
                  key: 'destination',
                  label: (
                    <IconMenuLabel>
                      <MapPin size={14} />
                      {item.destinationLocationId
                        ? 'Edit Destination'
                        : 'Suggest Destination'}
                    </IconMenuLabel>
                  ),
                  onClick: () => openSuggestDestination(item),
                },
              ],
            }}
          >
            <Button icon={<PencilSimple size={14} />}>Edit</Button>
          </Dropdown>
          {item.destinationLocationId ? (
            <Button type="primary" onClick={() => openTransfer(item)}>
              Create Transfer
            </Button>
          ) : null}
          <Button
            type="text"
            danger
            icon={<X size={16} />}
            aria-label="Delete suggested transfer"
            onClick={() => openDeleteConfirm(item)}
          />
        </ActionButtons>
      ),
    },
  ];

  const pagination = {
    current: filter.page,
    pageSize: filter.limit,
    total: totalData,
    showSizeChanger: true,
    pageSizeOptions: [10, 25, 50, 100],
    showTotal: total => `${total} suggested transfers`,
    onChange: (page, pageSize) => {
      if (pageSize !== filter.limit) {
        filterChanged('limit', pageSize);
      } else {
        pageChanged(page);
      }
    },
  };

  return (
    <div className={selectedCount > 0 ? 'pb-4 mb-4' : ''}>
      <TableToolbar>
        <RefreshSuggestedTransfersButton
          onRefresh={startRefresh}
          disabled={!warehouseId}
          isProcessing={isProcessing}
        />
        <Checkbox
          checked={!!filter.hasUnassignedAllocations}
          onChange={event =>
            filterChanged(
              'hasUnassignedAllocations',
              event.target.checked ? true : undefined,
            )
          }
        >
          Only show transfers with unassigned allocations
        </Checkbox>
        <Input.Search
          allowClear
          placeholder="Search by Item, Location ..."
          value={filter.search || ''}
          onChange={event => setSearch(event.target.value)}
          style={{ maxWidth: 320 }}
        />
      </TableToolbar>
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={data}
        loading={loading}
        size="middle"
        tableLayout="fixed"
        pagination={pagination}
        sortDirections={['ascend', 'descend', 'ascend']}
        onChange={(_pagination, _filters, sorter, extra) => {
          if (extra.action !== 'sort') return;

          const single = Array.isArray(sorter) ? sorter[0] : sorter;
          const field = single?.field || 'suggestedQuantity';
          onSortChange({
            [String(field)]:
              single?.order === 'ascend'
                ? CommonConstant.sortDirection.ascending
                : CommonConstant.sortDirection.descending,
          });
        }}
        style={{
          background: COLOR.white,
          borderRadius: 6,
          border: `1px solid ${COLOR.border}`,
          overflow: 'hidden',
        }}
      />
      <TransferPopup
        warehouseId={warehouseId}
        open={itemToTransfer}
        refresh={refresh}
        onClose={() => setItemToTransfer(null)}
        item={itemToTransfer}
      />
      <DestinationSuggestionModal
        open={openSuggestDestinationPopup}
        onClose={handleCloseSuggestDestinationPopup}
        item={itemToSuggestDestination}
        refresh={refresh}
      />
      <SuggestedTransferQuantityModal
        open={!!itemToEditQuantity}
        item={itemToEditQuantity}
        onClose={() => setItemToEditQuantity(null)}
        onUpdated={handleQuantityUpdated}
      />
      <OriginSuggestionModal
        open={!!itemToEditOrigin}
        item={itemToEditOrigin}
        onClose={() => setItemToEditOrigin(null)}
        refresh={refresh}
      />
      <ConfirmationPopup
        open={Boolean(pendingDelete)}
        onClose={closeDeleteConfirm}
        onConfirm={confirmDeleteSuggestedTransfer}
        loading={deleteProcessing}
        title="Delete suggested transfer"
        message={deleteMessage || ''}
        confirmText="Delete"
      />
      <BulkSuggestedTransferConfirmModal
        open={bulkConfirmOpen}
        onClose={() => setBulkConfirmOpen(false)}
        items={selectedItems}
        warehouseId={warehouseId}
        onDeselect={onBulkDeselect}
        onCompleted={handleBulkCompleted}
        onRefreshSelection={refreshAsync}
      />
      {selectedCount > 0 ? (
        <AlertFooterBar
          message={
            selectedCount === 1
              ? '1 suggested transfer selected'
              : `${selectedCount} suggested transfers selected`
          }
          cancel={clearBulkSelection}
          cancelButtonText="Clear"
          save={() => setBulkConfirmOpen(true)}
          saveButtonText="Create transfer batch"
        />
      ) : null}
    </div>
  );
}
